/**
 * Parses a week series string (e.g., "38-40,42,45") into an array of week numbers.
 * @param {string} weekSeriesStr - The string representation of week series.
 * @returns {number[]} An array of week numbers. Returns empty array for invalid input.
 */
export const parseWeekSeries = (weekSeriesStr) => {
    if (!weekSeriesStr || typeof weekSeriesStr !== 'string') {
        return [];
    }

    const weekNumbers = new Set();
    const parts = weekSeriesStr.split(',');

    for (const part of parts) {
        const trimmedPart = part.trim();
        if (trimmedPart.includes('-')) {
            const [startStr, endStr] = trimmedPart.split('-');
            const start = parseInt(startStr, 10);
            const end = parseInt(endStr, 10);

            // Validate that start and end are valid week numbers (1-53)
            if (!isNaN(start) && !isNaN(end) && start >= 1 && start <= 53 && end >= 1 && end <= 53) {
                if (start <= end) { // Normal range, e.g., 38-40
                    for (let i = start; i <= end; i++) {
                        weekNumbers.add(i);
                    }
                } else { // Wrap-around range, e.g., 52-4
                    for (let i = start; i <= 53; i++) { // Add weeks from start to 53
                        weekNumbers.add(i);
                    }
                    for (let i = 1; i <= end; i++) { // Add weeks from 1 to end
                        weekNumbers.add(i);
                    }
                }
            } else {
                console.warn(`[timeUtils] Invalid numbers in week range part: ${trimmedPart}. Start: ${startStr}, End: ${endStr}`);
            }
        } else {
            const weekNum = parseInt(trimmedPart, 10);
            if (!isNaN(weekNum) && weekNum >= 1 && weekNum <= 53) {
                weekNumbers.add(weekNum);
            } else {
                console.warn(`[timeUtils] Invalid week number in series: ${trimmedPart}`);
            }
        }
    }
    return Array.from(weekNumbers).sort((a, b) => a - b);
};

/**
 * Calculates the start and end Date objects for a given ISO week number within a year.
 * Uses proper ISO week numbering where week 1 contains the first Thursday of the year.
 * @param {number} year - The full year (e.g., 2024).
 * @param {number} weekNumber - The ISO week number (1-53).
 * @returns {{startDate: Date, endDate: Date} | null} The date range or null if inputs are invalid.
 */
export const getWeekDateRange = (year, weekNumber) => {
    if (isNaN(year) || isNaN(weekNumber) || weekNumber < 1 || weekNumber > 53) {
        console.warn(`[timeUtils] Invalid year or weekNumber for getWeekDateRange: year=${year}, week=${weekNumber}`);
        return null;
    }

    // Calculate the date of the first Thursday of the year (which is always in week 1 by ISO definition)
    const jan4 = new Date(year, 0, 4); // January 4th is always in week 1
    const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7 for ISO week
    
    // Find the Monday of week 1 (ISO weeks start on Monday)
    const firstMondayOfYear = new Date(jan4);
    firstMondayOfYear.setDate(jan4.getDate() - dayOfWeek + 1);
    
    // Calculate the start date of the target week
    const startDate = new Date(firstMondayOfYear);
    startDate.setDate(firstMondayOfYear.getDate() + (weekNumber - 1) * 7);
    
    // Calculate the end date (Sunday of the target week)
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    return { startDate, endDate };
};

/**
 * Checks if two date ranges overlap.
 * A null or undefined startDate or endDate in a range means it cannot overlap.
 * @param {{startDate?: Date | string, endDate?: Date | string} | null} range1 - The first date range.
 * @param {{startDate?: Date | string, endDate?: Date | string} | null} range2 - The second date range.
 * @returns {boolean} True if the ranges overlap, false otherwise.
 */
export const doDateRangesOverlap = (range1, range2) => {
    if (!range1 || !range2) {
        return false;
    }

    // Ensure dates are Date objects
    const r1Start = range1.startDate instanceof Date ? range1.startDate : (range1.startDate ? new Date(range1.startDate) : null);
    const r1End = range1.endDate instanceof Date ? range1.endDate : (range1.endDate ? new Date(range1.endDate) : null);
    const r2Start = range2.startDate instanceof Date ? range2.startDate : (range2.startDate ? new Date(range2.startDate) : null);
    const r2End = range2.endDate instanceof Date ? range2.endDate : (range2.endDate ? new Date(range2.endDate) : null);

    if (!r1Start || !r1End || !r2Start || !r2End) {
        // If any date is null or invalid, they cannot definitively overlap.
        return false;
    }
    
    // Check for invalid date objects (e.g. from new Date("invalid string"))
    if (isNaN(r1Start.getTime()) || isNaN(r1End.getTime()) || isNaN(r2Start.getTime()) || isNaN(r2End.getTime())) {
        return false;
    }

    return r1Start <= r2End && r1End >= r2Start;
}; 